import React, { useState } from "react";
import { Drawer, Toolbar, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Assets
import CalendarGif from "../../assets/CALENDAR2.gif";
import CalendarIcon from "../../assets/CalendarIcon.png";
import FeedbackGif from "../../assets/FEEDBACK2.gif";
import FeedbackIcon from "../../assets/FeedbackIcon.png";
import LibraryGif from "../../assets/LIBRARY2.gif";
import LibraryIcon from "../../assets/LibraryIcon.png";
import TrainingsPlanGif from "../../assets/TRAININGSPLAN2.gif";
import TrainingsPlanIcon from "../../assets/TrainingsPlanIcon.png";

/**
 * NavigationSidebar Component (Refactored with React Router)
 *
 * Uses React Router's navigate for proper URL-based navigation
 *
 * @param {Object} props - Component props
 * @param {string} props.currentContent - Currently active content section (derived from URL)
 * @returns {JSX.Element} NavigationSidebar component
 */
const NavigationSidebar = ({ currentContent }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Hover state for button animations
  const [hoverStatus, setHoverStatus] = useState({
    schedule: false,
    feedback: false,
    library: false,
    settings: false,
  });

  /**
   * Navigation items configuration
   */
  const navigationItems = [
    {
      id: "schedule",
      label: t("trainingPlan"),
      staticIcon: CalendarIcon,
      hoverGif: CalendarGif,
      path: "/home/calendar",
    },
    {
      id: "feedback",
      label: t("feedback"),
      staticIcon: FeedbackIcon,
      hoverGif: FeedbackGif,
      path: "/home/feedback",
    },
    {
      id: "library",
      label: t("library"),
      staticIcon: LibraryIcon,
      hoverGif: LibraryGif,
      path: "/home/library",
    },
    {
      id: "settings",
      label: t("settings"),
      staticIcon: TrainingsPlanIcon,
      hoverGif: TrainingsPlanGif,
      path: "/home/settings",
    },
  ];

  /**
   * Handles mouse enter for hover effects
   */
  const handleMouseEnter = (itemId) => {
    setHoverStatus((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  /**
   * Handles mouse leave for hover effects
   */
  const handleMouseLeave = (itemId) => {
    setHoverStatus((prev) => ({
      ...prev,
      [itemId]: false,
    }));
  };

  /**
   * Handles navigation click
   */
  const handleNavigate = (path) => {
    navigate(path);
  };

  /**
   * Creates a navigation button with hover effects
   */
  const createNavigationButton = (item, isLast = false) => (
    <Button
      key={item.id}
      className={`nav-button-${item.id}`}
      startIcon={
        <img
          src={hoverStatus[item.id] ? item.hoverGif : item.staticIcon}
          className="nav-icon"
          style={{
            position: "relative",
            width: "52px",
            height: "52px",
            marginLeft: "16px",
            marginRight: "16px",
            transition: "opacity 0.2s ease",
          }}
          alt={`${item.label} icon`}
        />
      }
      sx={{
        color: "black",
        backgroundColor: currentContent === item.id ? "#fbfbfb" : "#c8c8d3",
        fontWeight: currentContent === item.id ? "bold" : "normal",
        fontSize: 16,
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.2,
        borderRadius: "12px",
        marginBottom: isLast ? 10 : "8px",
        justifyContent: "flex-start",
        textTransform: "none",
        height: "72px",
        width: "70%",
        minWidth: "240px",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "#fbfbfb",
          fontWeight: "bold",
          transform: "translateX(4px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        },
        "&:active": {
          transform: "translateX(2px)",
        },
        "&:focus": {
          outline: "2px solid #063bd0",
          outlineOffset: "2px",
        },
      }}
      onMouseEnter={() => handleMouseEnter(item.id)}
      onMouseLeave={() => handleMouseLeave(item.id)}
      onClick={() => handleNavigate(item.path)}
      aria-label={`Navigate to ${item.label}`}
      aria-pressed={currentContent === item.id}
    >
      {item.label}
    </Button>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        zIndex: 0,
        display: "flex",
        flexDirection: "column",
        width: "25%",
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: "25%",
          boxSizing: "border-box",
          background: "transparent",
        },
      }}
    >
      <Toolbar />
      <Box sx={{ flexGrow: 1 }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 2,
          alignItems: "flex-end",
          px: 2,
          gap: 0.5,
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        {navigationItems.map((item, index) =>
          createNavigationButton(item, index === navigationItems.length - 1)
        )}
      </Box>
    </Drawer>
  );
};

export default NavigationSidebar;
